import { Webhook } from "lucide-react";
import { useState } from "react";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { EnumSelect } from "@/components/shared/enum-select";
import { ErrorState } from "@/components/shared/error-state";
import { CursorPagination } from "@/components/shared/pagination";
import { SearchInput } from "@/components/shared/search-input";
import { Badge } from "@/components/ui/badge";
import { useBuInstanceLabel } from "@/hooks/use-bu-instance-label";
import { useActiveBuInstances } from "@/hooks/use-bu-instances";
import { DEFAULT_LIMIT, useDataGrid } from "@/hooks/use-data-grid";
import { useSoapCallLog } from "@/hooks/use-soap-call-log";
import { SOAP_CALL_STATUS_OPTIONS, SOAP_OPERATION_OPTIONS } from "@/lib/enums";
import { formatDateTime } from "@/lib/format";
import { SoapCallLogDetailSheet } from "./SoapCallLogDetailSheet";

/**
 * Admin-only SOAP call log grid — mirrors `AuditLogTable.tsx`/
 * `MasterDataMappingsTable.tsx`'s shape. `grid.status` maps directly onto
 * the endpoint's `status` filter (success/error); `operation`/BU instance
 * are this grid's own filters, appended manually (the
 * `sourceBuFilter`/`targetBuFilter` pattern in `MasterDataMappingsTable`).
 * Row click opens `SoapCallLogDetailSheet`, which lazily fetches the full
 * request/response XML for just that row.
 */
export function SoapCallLogTable() {
  const { label } = useBuInstanceLabel();
  const grid = useDataGrid({ defaultSort: "createdAt" });
  const [operationFilter, setOperationFilter] = useState("");
  const [buInstanceFilter, setBuInstanceFilter] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: activeBuInstances = [] } = useActiveBuInstances();
  const buFilterOptions = activeBuInstances.map((bu) => ({ value: bu.id, label: bu.name }));

  const { data, isLoading, isError, error, refetch } = useSoapCallLog(
    grid.query,
    grid.pageRequest,
    {
      operation: operationFilter || undefined,
      buInstanceId: buInstanceFilter || undefined,
    },
  );

  const rows = data?.data ?? [];

  function handleOperationFilterChange(value: string) {
    setOperationFilter(value);
    grid.resetToFirstPage();
  }

  function handleBuInstanceFilterChange(value: string) {
    setBuInstanceFilter(value);
    grid.resetToFirstPage();
  }

  const toolbar = (
    <div className="flex flex-wrap items-center gap-4">
      <SearchInput
        value={grid.searchInput}
        onValueChange={grid.setSearch}
        placeholder={`Search by URL, initiator, or ${label}...`}
        className="flex-1 max-w-sm"
      />
      <EnumSelect
        value={grid.status}
        onValueChange={grid.setStatus}
        options={SOAP_CALL_STATUS_OPTIONS}
        allOption={{ label: "All statuses" }}
        placeholder="All statuses"
        className="w-[160px]"
        aria-label="Filter by status"
      />
      <EnumSelect
        value={operationFilter}
        onValueChange={handleOperationFilterChange}
        options={SOAP_OPERATION_OPTIONS}
        allOption={{ label: "All operations" }}
        placeholder="All operations"
        className="w-[180px]"
        aria-label="Filter by operation"
      />
      <EnumSelect
        value={buInstanceFilter}
        onValueChange={handleBuInstanceFilterChange}
        options={buFilterOptions}
        allOption={{ label: `Any ${label}` }}
        placeholder={`Any ${label}`}
        className="w-[180px]"
        aria-label={`Filter by ${label}`}
      />
    </div>
  );

  if (isError) {
    return (
      <div className="space-y-4">
        {toolbar}
        <ErrorState
          message={error?.message ?? "Failed to load SOAP call log"}
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {toolbar}

      <DataTable
        headers={[
          { label: "When", sortKey: "createdAt" },
          { label: "Operation", sortKey: "operation" },
          { label, className: "hidden md:table-cell" },
          { label: "Status", sortKey: "status" },
          { label: "HTTP", className: "hidden sm:table-cell", sortKey: "httpStatus" },
          { label: "Duration", className: "hidden sm:table-cell", sortKey: "durationMs" },
          { label: "Initiator", className: "hidden lg:table-cell" },
          { label: "Error", className: "hidden lg:table-cell" },
        ]}
        sort={grid.sort}
        sortDir={grid.sortDir}
        onSort={grid.toggleSort}
        loading={isLoading}
        empty={
          <EmptyState
            icon={Webhook}
            title="No SOAP calls logged"
            description="Coach Manager calls (Login, ContractSearch, InsertContract) will appear here as they happen."
          />
        }
      >
        {rows.map((entry) => (
          <tr
            key={entry.id}
            onClick={() => setSelectedId(entry.id)}
            className="cursor-pointer border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
          >
            <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
              {formatDateTime(entry.createdAt)}
            </td>
            <td className="px-4 py-3 font-medium">{entry.operation}</td>
            <td className="hidden md:table-cell px-4 py-3 text-muted-foreground">
              {entry.buInstanceName ?? "—"}
            </td>
            <td className="px-4 py-3">
              <Badge variant={entry.status === "success" ? "success" : "destructive"}>
                {entry.status === "success" ? "Success" : "Error"}
              </Badge>
            </td>
            <td className="hidden sm:table-cell px-4 py-3 text-muted-foreground">
              {entry.httpStatus ?? "—"}
            </td>
            <td className="hidden sm:table-cell px-4 py-3 text-muted-foreground">
              {entry.durationMs}ms
            </td>
            <td className="hidden lg:table-cell px-4 py-3 text-muted-foreground max-w-[200px] truncate">
              {entry.initiatorEmail ?? entry.initiatorId ?? "—"}
            </td>
            <td className="hidden lg:table-cell px-4 py-3 text-muted-foreground max-w-xs truncate">
              {entry.errorMessage ?? "—"}
            </td>
          </tr>
        ))}
      </DataTable>

      <CursorPagination
        total={data?.total ?? 0}
        page={grid.page}
        limit={DEFAULT_LIMIT}
        hasNext={data?.hasNext ?? false}
        hasPrev={data?.hasPrev ?? false}
        onNext={() => grid.goNext(data?.nextCursor ?? null)}
        onPrev={() => grid.goPrev(data?.prevCursor ?? null)}
        loading={isLoading}
        itemLabel="calls"
      />

      <SoapCallLogDetailSheet id={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
}
